import { useSyncExternalStore, type ReactNode } from "react";

export type DrawerSize = "small" | "medium";

export type AfterCloseCallback = () => void;

export type OpenDrawer = (
  content: ReactNode,
  title: string,
  size?: DrawerSize,
  onAfterClose?: AfterCloseCallback
) => void;

type DrawerState = {
  isOpen: boolean;
  isClosing: boolean;
  content: ReactNode;
  title: string;
  size: DrawerSize;
};

// Matches the Sheet close animation (data-[state=closed]:duration-300).
const CLOSE_ANIMATION_MS = 300;

// Module-level store: a single app-wide drawer driven imperatively, no context
// or provider. Consumers read state via useSyncExternalStore; row-click utils
// and other non-component code can call openDrawer/closeDrawer directly.
let state: DrawerState = {
  isOpen: false,
  isClosing: false,
  content: null,
  title: "",
  size: "medium",
};
let afterClose: AfterCloseCallback | undefined;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

const setState = (partial: Partial<DrawerState>) => {
  state = { ...state, ...partial };
  emit();
};

export const openDrawer: OpenDrawer = (
  content,
  title,
  size = "medium",
  onAfterClose
) => {
  afterClose = onAfterClose;
  setState({ content, title, size, isOpen: true, isClosing: false });
};

export const closeDrawer = () => {
  if (!state.isOpen && !state.isClosing) return;
  const onAfterClose = afterClose;
  afterClose = undefined;
  setState({ isOpen: false, isClosing: true });
  onAfterClose?.();
  window.setTimeout(() => setState({ isClosing: false }), CLOSE_ANIMATION_MS);
};

export const subscribeToDrawer = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getDrawerSnapshot = () => state;

/**
 * Imperative app-wide drawer. Call openDrawer(content, title, size?, onAfterClose?)
 * from anywhere. Mount <DrawerRoot /> once at the app root to render it.
 */
export const useDrawer = () => {
  const snapshot = useSyncExternalStore(subscribeToDrawer, getDrawerSnapshot);
  return {
    openDrawer,
    closeDrawer,
    isOpen: snapshot.isOpen,
    isClosing: snapshot.isClosing,
  };
};
